import React, { useState } from 'react';
import produce from 'immer';
import { DayEnum, OpeningHourDefinitionsInDTO, OpeningHoursDayInDTO, OpeningModeEnum } from '@api/ringil-api.v2';
import { ValidationError } from 'yup';
import OpeningHoursDayRow from './OpeningHoursDayRow';
import GenerateDialog, { GeneratedSlot } from '@components/obsolete/OpeningHoursDefinition/GenereateDialog';

// Validation is pure yup logic with no UI — reuse the (still authoritative) obsolete schema until it is promoted.
export { openingHoursValidationSchema, emptyOpeningHours } from '@components/obsolete/OpeningHoursDefinition/OpeningHourDefinitionForm';

interface ComponentProps {
  value: OpeningHourDefinitionsInDTO;
  onChange: (value: OpeningHourDefinitionsInDTO) => void;
  validationErrors?: ValidationError[];
  validationPrefix?: string;
}

const LocationOpeningHoursEditor: React.FC<ComponentProps> = ({ value, onChange, validationErrors, validationPrefix = '' }) => {
  const [generateForDay, setGenerateForDay] = useState<DayEnum | null>(null);

  const mutateDay = (day: DayEnum, fn: (d: OpeningHoursDayInDTO) => void) =>
    onChange(
      produce(value, draft => {
        const d = draft.openingHoursDays.find(od => od.day === day);
        if (d) fn(d);
      }),
    );

  const onOpen = (day: DayEnum) =>
    mutateDay(day, d => {
      d.openingMode = OpeningModeEnum.PARTIALLY_OPEN;
      d.openingHours = [{ since: '', till: '' }];
    });

  const onClose = (day: DayEnum) =>
    mutateDay(day, d => {
      d.openingMode = OpeningModeEnum.CLOSED;
    });

  const onToggleAllDay = (day: DayEnum) =>
    mutateDay(day, d => {
      if (d.openingMode === OpeningModeEnum.OPEN) {
        d.openingMode = OpeningModeEnum.PARTIALLY_OPEN;
        d.openingHours = [{ since: '', till: '' }];
      } else {
        d.openingMode = OpeningModeEnum.OPEN;
        d.openingHours = [{ since: '00:00', till: '24:00' }];
      }
    });

  const onAddInterval = (day: DayEnum) =>
    mutateDay(day, d => {
      d.openingHours = [...(d.openingHours ?? []), { since: '', till: '' }];
    });

  const onRemoveInterval = (day: DayEnum, index: number) =>
    mutateDay(day, d => {
      d.openingHours?.splice(index, 1);
    });

  // Match the legacy guard: only accept characters that can form a time string.
  const onChangeSince = (day: DayEnum, index: number, time: string) => {
    if (time.match(/[0-9:]*/)[0] === time) mutateDay(day, d => d.openingHours && (d.openingHours[index].since = time));
  };

  const onChangeTill = (day: DayEnum, index: number, time: string) => {
    if (time.match(/[0-9:]*/)[0] === time) mutateDay(day, d => d.openingHours && (d.openingHours[index].till = time));
  };

  const onSlotsGenerated = (day: DayEnum, slots: GeneratedSlot[]) =>
    mutateDay(day, d => {
      d.openingMode = OpeningModeEnum.PARTIALLY_OPEN;
      d.openingHours = slots.map(s => ({ since: s.sinceHourString, till: s.tillHourString }));
    });

  return (
    <div className="flex w-full flex-col gap-4">
      {value?.openingHoursDays?.map((day, index) => (
        <OpeningHoursDayRow
          key={day.day}
          day={day}
          dayIndex={index}
          validationErrors={validationErrors}
          validationPrefix={validationPrefix}
          onOpen={() => onOpen(day.day)}
          onClose={() => onClose(day.day)}
          onToggleAllDay={() => onToggleAllDay(day.day)}
          onAddInterval={() => onAddInterval(day.day)}
          onRemoveInterval={i => onRemoveInterval(day.day, i)}
          onChangeSince={(i, v) => onChangeSince(day.day, i, v)}
          onChangeTill={(i, v) => onChangeTill(day.day, i, v)}
          onGenerate={() => setGenerateForDay(day.day)}
        />
      ))}
      <GenerateDialog
        generateForDay={generateForDay}
        onHide={() => setGenerateForDay(null)}
        onGenerateSlots={slots => {
          onSlotsGenerated(generateForDay, slots);
          setGenerateForDay(null);
        }}
      />
    </div>
  );
};

export default LocationOpeningHoursEditor;
